import { FC } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  findBy,
  FormControl,
  FormHelperText,
  InputLabel,
  makeStyles,
  MenuItem,
  MuiStack,
  Select,
  TextField,
  Typography,
} from '@perk-ui/core'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import { matchSorter } from 'match-sorter'
// import { useQueryClient } from 'react-query'
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router'
import * as yup from 'yup'

import { useSitesContext } from '../../../components/SitesProvider'
// import QueryKeys from '../../../features/query-hooks/QueryKeys'
import useCreateTreatment from '../../../features/query-hooks/useCreateTreatment'
import useMedicalTreatments from '../../../features/query-hooks/useMedicalTreatments'
import usePatientCases from '../../../features/query-hooks/usePatientCases'
import useProviders from '../../../features/query-hooks/useProviders'
import useTreatmentProducts from '../../../features/query-hooks/useTreatmentProducts'
import { buildName } from '../../../utils/buildName'
import { TreatmentTypeNameCustomizations } from '../../PatientFlow/Treatment/Treatment'
import PatientSelfOnboardUrls, {
  PatientSelfOnboardParams,
} from '../PatientSelfOnboardUrls'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '787px',
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  form: {
    marginTop: theme.spacing(2),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
    '& > .MuiDivider-root': {
      marginBottom: theme.spacing(3),
    },
  },
  loadingContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(6),
  },
  sectionTitle: {
    textAlign: 'center',
    fontFamily: 'DM Sans',
    fontSize: '28px',
    lineHeight: '24px',
  },
  goBackButton: {
    border: '1px solid #90CAF9', // Light blue border
    borderRadius: '8px', // Rounded corners
    color: '#42A5F5 !important', // Blue text color
    textColor: '#42A5F5', // Blue text color
    textTransform: 'none', // Prevents all-uppercase text
    // fontWeight: 500, // Semi-bold text
    backgroundColor: 'white', // White background
    '&:hover': {
      backgroundColor: '#E3F2FD', // Light blue background on hover
    },
  },
}))

export interface TreatmentSessionProps {}

export interface TreatmentSessionFormValues {
  parentId?: string
  siteLocationId?: string
  providerId: string
  medicalTreatmentId: string
  treatmentProductId?: string
  treatmentDate: string
}
/**
 * TODO: Refactor this component to use Routes to handle its internal `pageState`
 */
const TreatmentSession: FC<TreatmentSessionProps> = () => {
  const classes = useStyles()
  const { push, goBack } = useHistory()
  // const queryClient = useQueryClient()
  const hasCaseData = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.addSession.match,
  )
  const { patientId, caseId, treatmentId } = hasCaseData?.params || {}

  const createTreatment = useCreateTreatment()
  const { data: providers = [] } = useProviders()
  const { currentSite } = useSitesContext()

  const {
    data: patientCases = [],
    isLoading: isLoadingCases,
  } = usePatientCases(patientId)
  const {
    data: medicalTreatments = [],
    isLoading: isLoadingMedicalTreatments,
  } = useMedicalTreatments()
  const { data: treatmentProducts = [] } = useTreatmentProducts(
    medicalTreatments.find((t) => t.slug === 'shockwave-therapy')?.id || '',
  )
  const location = useLocation<{ from?: string }>()
  const isContura = currentSite?.organization?.name === 'Contura'
  const isEmCyte = currentSite?.organization?.name === 'EmCyte'
  const isStemWave = currentSite?.organization?.name === 'StemWave'

  const patientCase = findBy('id', caseId, patientCases)

  const isLoading = isLoadingCases || isLoadingMedicalTreatments

  const validationSchema = yup.object({
    providerId: yup.string().defined('A provider must be selected'),
    medicalTreatmentId: yup
      .string()
      .defined('A treatment type must be selected'),
    treatmentProductId: yup
      .string()
      .defined('A treatment product must be selected'),
  })

  const {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    handleBlur,
    handleSubmit,
    setFieldValue,
  } = useFormik<TreatmentSessionFormValues>({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      parentId: treatmentId,
      providerId: '',
      medicalTreatmentId:
        medicalTreatments.find((t) => t.slug === 'shockwave-therapy')?.id || '',
      treatmentProductId:
        treatmentProducts.find((tp) => tp.name === 'StemWave')?.id || '',
      siteLocationId: '',
      treatmentDate: format(new Date(), 'yyyy-MM-dd'),
    },
    onSubmit: async (values) => {
      if (!patientId || !caseId) return
      if (currentSite?.siteLocations.length === 1) {
        values.siteLocationId = currentSite.siteLocations[0].id
      }

      return createTreatment
        .mutateAsync({
          patientCaseId: caseId,
          ...values,
        })
        .then((result) => {
          if (result.id) {
            // queryClient.invalidateQueries(
            //   QueryKeys.surveyResponseGroupsAlt({
            //     currentSiteId: currentSite?.id || '',
            //     patientId,
            //     patientCaseId: caseId,
            //   }),
            // )
            push(PatientSelfOnboardUrls.survey.create(patientId, caseId))
          }
        })
    },
  })

  // Determine if fields should be disabled for TreatmentSession
  const shouldDisableFields = !(isEmCyte || (isContura && !patientCase?.study))

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (
    patientCase &&
    patientCase.patientDiagnoses &&
    patientCase?.patientDiagnoses.length === 0 &&
    !location.state?.from?.includes('/diagnosis')
  ) {
    return (
      <Redirect
        to={{
          pathname: PatientSelfOnboardUrls.addDiagnoses.create(
            patientCase.patientId,
            patientCase.id,
          ),
          state: { from: location.state?.from ? location.state?.from : null },
        }}
      />
    )
  }

  const availableMedicalTreatmentOptions = medicalTreatments.filter(
    (treatment) => {
      // Keep existing StemWave behavior unchanged
      if (isStemWave) {
        if (patientCase?.study) {
          if (patientCase.patientTreatments?.length == 0) {
            return patientCase?.study?.medicalTreatments
              .map((mt) => mt.id)
              .includes(treatment.id)
          } else {
            return (
              treatment.onlyForStudy == false ||
              patientCase?.study?.medicalTreatments
                .map((mt) => mt.id)
                .includes(treatment.id)
            )
          }
        }
        return treatment.onlyForStudy == false
      }

      // Simplified filtering for non-StemWave: Study -> All Fields
      // Priority 1: If study selected, show only study fields
      if (patientCase?.study) {
        if (patientCase.patientTreatments?.length == 0) {
          return patientCase?.study?.medicalTreatments
            .map((mt) => mt.id)
            .includes(treatment.id)
        } else {
          return (
            treatment.onlyForStudy == false ||
            patientCase?.study?.medicalTreatments
              .map((mt) => mt.id)
              .includes(treatment.id)
          )
        }
      }

      // Priority 2: Show all non-study-only treatments (organization filtering will be on products)
      return treatment.onlyForStudy == false
    },
  )

  const availableTreatmentProductOptions = treatmentProducts.filter(
    (product) => {
      // Keep existing StemWave behavior unchanged
      if (isStemWave) {
        if (
          patientCase?.study &&
          patientCase?.study.slug !==
            'platelet-rich-plasma-and-the-effects-of-nsaids-on-pain-and-functional-scores-in-knee-osteoarthritis'
        ) {
          if (patientCase.patientTreatments?.length == 0) {
            return patientCase?.study?.treatmentProducts
              .map((tp) => tp.id)
              .includes(product.id)
          } else {
            return (
              product.onlyForStudy == false ||
              patientCase?.study?.treatmentProducts
                .map((tp) => tp.id)
                .includes(product.id)
            )
          }
        }
        return product.onlyForStudy == false
      }

      // Study -> Org -> All Fields filtering for non-StemWave organizations
      // Priority 1: If study selected, show only study fields
      if (patientCase?.study) {
        if (patientCase.patientTreatments?.length == 0) {
          return patientCase?.study?.treatmentProducts
            .map((tp) => tp.id)
            .includes(product.id)
        } else {
          return (
            product.onlyForStudy == false ||
            patientCase?.study?.treatmentProducts
              .map((tp) => tp.id)
              .includes(product.id)
          )
        }
      }

      // Priority 2: If no study selected, show organization-specific products
      if (product.manufacturer === currentSite?.organization?.name) {
        return product.onlyForStudy == false
      }

      // Priority 3: Show all non-study-only products if no organization-specific products exist
      const hasOrgProducts = treatmentProducts.some(
        (p) =>
          p.manufacturer === currentSite?.organization?.name &&
          p.onlyForStudy == false,
      )
      if (!hasOrgProducts) {
        return product.onlyForStudy == false
      }

      // If org has products, only show org products (handled in Priority 2)
      return false
    },
  )

  return (
    <Box className={classes.root}>
      <Typography className={classes.sectionTitle}>
        <b>Treatment Details</b>
      </Typography>
      <form className={classes.form} onSubmit={handleSubmit}>
        {currentSite && currentSite.siteLocations.length > 1 && (
          <FormControl style={{ width: '100%' }} error={!!errors.providerId}>
            <InputLabel id="site-location-label" required>
              Clinic Location
            </InputLabel>
            <Select
              id="patient-flow-treatment-site-location"
              labelId="site-location-label"
              placeholder="Select"
              value={values.siteLocationId || ''}
              fullWidth
              required
              onChange={(event) => {
                setFieldValue('siteLocationId', event.target.value)
              }}
            >
              {currentSite?.siteLocations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
            {errors.siteLocationId && (
              <FormHelperText error>{errors.siteLocationId}</FormHelperText>
            )}
          </FormControl>
        )}
        <FormControl style={{ width: '100%' }} error={!!errors.providerId}>
          <InputLabel id="provider-label" required>
            Provider
          </InputLabel>
          <Select
            id="patient-flow-treatment-provider"
            labelId="provider-label"
            placeholder="Select"
            value={values.providerId}
            fullWidth
            required
            onChange={(event) => {
              setFieldValue('providerId', event.target.value)
            }}
          >
            {providers.map((prov) => (
              <MenuItem key={prov.id} value={prov.id}>
                {buildName(prov)}
              </MenuItem>
            ))}
          </Select>
          {errors.providerId && (
            <FormHelperText error>{errors.providerId}</FormHelperText>
          )}
        </FormControl>
        <Autocomplete
          id="patient-flow-treatment-medical-treatment"
          options={availableMedicalTreatmentOptions}
          getOptionLabel={(opt) =>
            opt.name in TreatmentTypeNameCustomizations
              ? TreatmentTypeNameCustomizations[opt.name]
              : opt.name
          }
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? matchSorter(options, inputValue, {
                  keys: [
                    'name',
                    (opt) => TreatmentTypeNameCustomizations[opt.name],
                  ],
                })
              : options
          }
          disabled={shouldDisableFields}
          value={
            availableMedicalTreatmentOptions.find(
              (treatment) => treatment.id === values.medicalTreatmentId,
            ) ?? null
          }
          onChange={(_event, treatment) => {
            setFieldValue('medicalTreatmentId', treatment?.id || '')
          }}
          renderInput={(params) => {
            // @ts-expect-error Mui's autocomplete types here are incomplete
            params.InputLabelProps.shrink = true
            return (
              <TextField
                {...params}
                label="Treatment Type"
                name="medicalTreatmentId"
                required
                value={values.medicalTreatmentId}
                onBlur={handleBlur}
                error={
                  touched.medicalTreatmentId &&
                  Boolean(errors.medicalTreatmentId)
                }
                helperText={
                  touched.medicalTreatmentId && errors.medicalTreatmentId
                }
              />
            )
          }}
        />
        <Autocomplete
          id="patient-flow-treatment-product"
          options={availableTreatmentProductOptions}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? matchSorter(options, inputValue, { keys: ['name'] })
              : options
          }
          disabled={shouldDisableFields}
          value={
            treatmentProducts.find(
              (prod) => prod.id === values.treatmentProductId,
            ) ?? null
          }
          onChange={(_event, prov) => {
            setFieldValue('treatmentProductId', prov?.id || '')
          }}
          renderInput={(params) => {
            // @ts-expect-error Mui's autocomplete types here are incomplete
            params.InputLabelProps.shrink = true
            return (
              <TextField
                {...params}
                name="treatmentProductId"
                label="Product Name"
                onBlur={handleBlur}
                required
                value={values.treatmentProductId}
                error={
                  touched.treatmentProductId &&
                  Boolean(errors.treatmentProductId)
                }
                helperText={
                  touched.treatmentProductId && errors.treatmentProductId
                }
              />
            )
          }}
        />
        <MuiStack
          direction="row"
          justifyContent="center"
          sx={{ marginTop: '40px', marginBottom: '40px' }}
        >
          <Button
            className={classes.goBackButton}
            disableElevation
            fullWidth
            style={{ marginRight: '20px' }}
            onClick={goBack}
          >
            Go Back
          </Button>
          {/* TODO: change submit form to validation and change step */}
          <Button
            fullWidth
            type="submit"
            loading={isSubmitting}
            disabled={!isValid}
          >
            Continue
          </Button>
        </MuiStack>
      </form>
    </Box>
  )
}

export default TreatmentSession
